import { type FC, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  DropdownPlaceholder,
  EnumDropdownItemVariant,
  type IDropdownItem,
  Typography,
} from 'shared/ui'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { numberFormat } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import { type CreditPrice } from 'entities/Billing/model/CreditPrice'
import { useEntitiesContext } from 'entities/EntitiesExport'
import styles from './styles.module.scss'

type IDropdownCreditsProps = {
  onChange?: (item: CreditPrice) => void
}

export const DropdownCredits: FC<IDropdownCreditsProps> = observer(({ onChange }) => {
  const [activeId, setActiveId] = useState<number | null>(null)
  const { billingStore, subscriptionStore } = useEntitiesContext()
  const activeItem = activeId ? billingStore.getCreditsPrice(activeId) : null
  const additionalCreditAmount = subscriptionStore.plan?.additional_credit_amount ?? 0
  const items: IDropdownItem[] = billingStore.creditsPrice.map((item) => {
    return {
      id: item.credits,
      label: `${item.credits}`,
      labelContent: (
        <Typography
          variant={'body-md-regular'}
          className={styles.dropdownItem}
          ariaLabel={'priceLabel'}
        >
          {item.creditsLabel} credits <span>(${item.priceLabel})</span>
        </Typography>
      ),
      activeValue: item.credits === activeId,
      variant: EnumDropdownItemVariant.CheckedRight,
    }
  })

  useEffect(() => {
    billingStore.fetchBillingAdditionalCreditPrice()
  }, [])

  useEffect(() => {
    if (items.length) {
      const itemPrice = billingStore.getCreditsPrice(Number(items[0].id))

      if (itemPrice && onChange) {
        setActiveId(itemPrice.credits)
        onChange(itemPrice)
      }
    }
  }, [items.length])

  return (
    <DropdownPlaceholder
      items={items}
      onChange={(item) => {
        const itemPrice = billingStore.getCreditsPrice(Number(item.id))

        if (itemPrice && onChange) {
          setActiveId(itemPrice.credits)
          onChange(itemPrice)
        }
      }}
      widthDropdown={440}
      placeholder={
        <div className={styles.dropdownActive}>
          <Typography variant={'text-input-field-xl'} ariaLabel={'creditsLabel'}>
            {activeItem?.creditsLabel}
          </Typography>
          <Typography
            variant={'text-input-field-r'}
            ariaLabel={'priceLabel'}
            className={styles.priceLabel}
          >
            ${activeItem?.priceLabel}
          </Typography>
        </div>
      }
      textPlaceholderProps={{
        className: styles.dropdownPlaceholder,
        placeholderClassName: styles.dropdownPlaceholderInner,
        variant: 'stroke',
        rightActions: [makeTextFieldIcon({ icon: 'chevronDown' })],
      }}
      label={{
        text: 'Credits',
        className: styles.dropdownLabel,
        rightText: (
          <Typography
            ariaLabel='additionalCreditAmount'
            variant='text-input-label-small'
            className={styles.additionalCreditAmount}
          >
            {`${numberFormat({
              value: additionalCreditAmount,
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            })} per credit`}
          </Typography>
        ),
      }}
      zIndex={ZIndex.OVERLAY_TOP_110}
    />
  )
})
